import { useCallback, useEffect, useRef } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';

import { downloadFromUrl } from 'src/v2/shared/ui/downloadFromUrl';

/** The slice of a presigned-export response this hook needs to decide whether to download. */
export interface PresignedExportResult {
  readonly downloadUrl?: string;
  readonly recordCount?: number;
}

export interface UsePresignedCsvExportResult {
  /** Generate the CSV export for the current scope and download it when it has rows. */
  readonly triggerExport: () => void;
  readonly isExporting: boolean;
  /** The last export attempt failed — surfaces until the next attempt. */
  readonly showError: boolean;
  /** The last export succeeded but had no rows — surfaces until the next attempt. */
  readonly showEmpty: boolean;
}

/**
 * Drives a server-generated CSV export that returns a short-lived presigned S3 URL: fire the
 * mutation, and when it resolves with rows, download the file. Shared by the Risk Engine's list
 * export and its drill-down (metric-group) export so the generate-then-download behaviour — the
 * mount guard and the error/empty banner flags included — lives in one place; each caller supplies
 * its own mutation and an input builder.
 *
 * `buildInput` returns `null` when the scope is not ready (e.g. no business unit), which no-ops the
 * trigger — pass a `useCallback`-stable builder so the returned `triggerExport` stays stable. The
 * `onSuccess` download re-checks that the hook is still mounted before firing: the mutation can
 * resolve after the user has navigated away, and a presigned URL opened then would be a surprise
 * download. `showError`/`showEmpty` derive from the mutation's status, so they reset on the next
 * attempt and never outlive their cause.
 */
export function usePresignedCsvExport<TInput, TResult extends PresignedExportResult>(
  mutation: UseMutationResult<TResult, Error, TInput>,
  buildInput: () => TInput | null,
): UsePresignedCsvExportResult {
  // The mutation can resolve after the page unmounts (user navigated away); the download side
  // effect must not fire once that originating intent is gone. Set the flag true in the effect
  // body (not just the initializer): under StrictMode React runs mount → cleanup → mount, so a
  // cleanup-only effect would leave the ref stuck false after the double-invoke and silently skip
  // every download.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const { mutate } = mutation;
  const triggerExport = useCallback((): void => {
    const input = buildInput();
    if (input === null) return;
    mutate(input, {
      onSuccess: (output) => {
        if (!isMountedRef.current) return;
        // An empty export has nothing to download — the empty banner covers that case.
        if ((output.recordCount ?? 0) > 0 && output.downloadUrl) {
          downloadFromUrl(output.downloadUrl);
        }
      },
    });
  }, [buildInput, mutate]);

  return {
    triggerExport,
    isExporting: mutation.isPending,
    showError: mutation.isError,
    showEmpty: mutation.isSuccess && (mutation.data?.recordCount ?? 0) === 0,
  };
}
